// User-scoped, client-only preference for the Updates side panel. Separate
// from Capacity's server-backed Scenario preference. Unknown users are not
// persisted; unavailable storage and invalid values fall back to Team.
import { useCallback, useEffect, useState } from "react";

export const UPDATES_PANEL_MODES = ["team", "media", "collapsed"];
const DEFAULT = { mode: "team", tab: "photos" };

export function updatesPanelKey(userKey) {
  return userKey ? `places.updatesPanel.v1:${userKey}` : null;
}

export function readUpdatesPanel(key) {
  if (!key) return { ...DEFAULT };
  try {
    const raw = JSON.parse(window.localStorage.getItem(key) || "null");
    return {
      mode: UPDATES_PANEL_MODES.includes(raw?.mode) ? raw.mode : DEFAULT.mode,
      tab: raw?.tab === "documents" ? "documents" : "photos",
    };
  } catch { return { ...DEFAULT }; }
}

export function useUpdatesPanelPreference(userKey) {
  const key = updatesPanelKey(userKey);
  // State is tagged with its owning key: after a user switch the first render
  // already reads the new user's value, and updates never merge another
  // user's state or write under a stale key.
  const [state, setState] = useState(() => ({ key, pref: readUpdatesPanel(key) }));
  const pref = state.key === key ? state.pref : readUpdatesPanel(key);
  useEffect(() => { setState((s) => (s.key === key ? s : { key, pref: readUpdatesPanel(key) })); }, [key]);
  const update = useCallback((patch) => {
    setState((s) => {
      const base = s.key === key ? s.pref : readUpdatesPanel(key);
      const next = { ...base, ...patch };
      if (!UPDATES_PANEL_MODES.includes(next.mode)) next.mode = "team";
      if (next.tab !== "documents") next.tab = "photos";
      if (key) { try { window.localStorage.setItem(key, JSON.stringify(next)); } catch { /* presentation only */ } }
      return { key, pref: next };
    });
  }, [key]);
  return [pref, update];
}
